import { FiAlertTriangle, FiEdit, FiEye, FiLock } from "react-icons/fi";
import {
  ErrorsByRecordType,
  getRecordTypeName,
} from "../hooks/useDiscrepancyPopup";

type AllErrorsTabProps = {
  errorsByRecordType: Record<string, ErrorsByRecordType>;
  errorMessageMap: Map<string, any>;
  onFixRecord?: (recordType: string) => void;
  viewPermissions?: Record<string, boolean>;
  editPermissions?: Record<string, boolean>;
};

export function AllErrorsTab({
  errorsByRecordType,
  errorMessageMap,
  onFixRecord,
  viewPermissions = {},
  editPermissions = {},
}: AllErrorsTabProps) {
  const canViewRecord = (recordType: string) =>
    viewPermissions[recordType] ?? true;
  const canEditRecord = (recordType: string) =>
    editPermissions[recordType] ?? false;

  const entries = Object.entries(errorsByRecordType);

  return (
    <div
      className="overflow-hidden"
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "0.375rem 0.375rem 0px 0px",
      }}
    >
      <div
        className="text-white p-3"
        style={{
          background: "#475569",
          fontWeight: 500,
          letterSpacing: "0.025em",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <FiAlertTriangle size={16} />
          <span>Related Errors</span>
        </div>
      </div>

      <div style={{ backgroundColor: "white" }}>
        {entries.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No related errors found.
          </div>
        ) : (
          entries.map(([recordType, data], index) => {
            const canView = canViewRecord(recordType);
            const canEdit = canEditRecord(recordType);

            return (
              <div
                key={recordType}
                className={index > 0 ? "border-top" : ""}
                style={{ padding: "20px 24px" }}
              >
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontWeight: 700, fontSize: "15px" }}>
                      {recordType}
                    </span>
                    <span
                      className="text-muted"
                      style={{ fontWeight: 400, fontSize: "15px" }}
                    >
                      ({getRecordTypeName(recordType)})
                    </span>
                    <span
                      style={{
                        backgroundColor: "rgb(254, 226, 226)",
                        color: "rgb(153, 27, 27)",
                        fontWeight: 600,
                        fontSize: "11px",
                        padding: "3px 10px",
                        borderRadius: "9999px",
                        border: "1px solid rgb(245, 128, 128)",
                      }}
                    >
                      {data.errors.length}{" "}
                      {data.errors.length === 1 ? "error" : "errors"}
                    </span>
                  </div>
                  {canView && (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-1"
                      aria-label={
                        canEdit
                          ? `Fix ${recordType} record`
                          : `View ${recordType} record`
                      }
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #E5E7EB",
                        backgroundColor: "white",
                        color: "#374151",
                        padding: "6px 12px",
                        fontWeight: 500,
                        fontSize: "13px",
                      }}
                      onClick={() => onFixRecord?.(recordType)}
                    >
                      {canEdit ? <FiEdit size={14} /> : <FiEye size={14} />}
                      <span>{canEdit ? "Fix" : "View"}</span>
                    </button>
                  )}
                </div>
                {canView ? (
                  Array.from(data.uniqueErrorCodes).map((errorCode) => (
                    <div
                      key={errorCode}
                      className="mb-2"
                      style={{ paddingLeft: "8px" }}
                    >
                      <span
                        style={{
                          color: "#DC2626",
                          fontWeight: 500,
                          fontSize: "14px",
                        }}
                      >
                        {errorCode}
                      </span>
                      <span
                        style={{
                          color: "#374151",
                          fontWeight: 400,
                          fontSize: "14px",
                        }}
                      >
                        {" "}
                        —{" "}
                        {errorMessageMap.get(errorCode)?.ErrorMessageText ??
                          "--"}
                      </span>
                    </div>
                  ))
                ) : (
                  <div
                    className="d-flex align-items-center gap-2 text-muted"
                    style={{ paddingLeft: "8px", fontStyle: "italic" }}
                  >
                    <FiLock size={14} />
                    <span>No access for {recordType} records</span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
